import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'

export type Role = 'admin' | 'tenant_admin' | 'user'

export interface User {
  id: string
  /** 已退役为显示字段（新账号 = email）；展示请优先用 display_name */
  username: string
  display_name?: string
  email: string
  role: Role
  tenant_id: string
  created_at: string
}

const AUTH_TOKEN_KEY = 'guardrail_token'
const AUTH_REFRESH_KEY = 'guardrail_refresh_token'
const AUTH_USER_KEY = 'guardrail_user'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isTenantAdmin = computed(() => user.value?.role === 'tenant_admin')
  const isUser = computed(() => user.value?.role === 'user')
  const canEdit = computed(() => user.value?.role === 'admin' || user.value?.role === 'tenant_admin')
  const canManageUsers = computed(() => user.value?.role === 'admin' || user.value?.role === 'tenant_admin')
  const tenantId = computed(() => user.value?.tenant_id || '')
  const userId = computed(() => user.value?.id || '')

  function checkAuth() {
    const savedToken = localStorage.getItem(AUTH_TOKEN_KEY)
    const savedUser = localStorage.getItem(AUTH_USER_KEY)
    const savedRefresh = localStorage.getItem(AUTH_REFRESH_KEY)
    if (savedToken && savedUser) {
      try {
        token.value = savedToken
        user.value = JSON.parse(savedUser)
        refreshToken.value = savedRefresh
      } catch {
        clearAuth()
      }
    }
  }

  async function login(email: string, password: string) {
    const res = await authApi.login({ email, password })
    token.value = res.access_token
    refreshToken.value = res.refresh_token
    user.value = res.user
    localStorage.setItem(AUTH_TOKEN_KEY, res.access_token)
    localStorage.setItem(AUTH_REFRESH_KEY, res.refresh_token)
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.user))
  }

  function logout() {
    clearAuth()
  }

  function loginWithToken(accessToken: string, refresh: string, userData: User) {
    token.value = accessToken
    refreshToken.value = refresh
    user.value = userData
    localStorage.setItem(AUTH_TOKEN_KEY, accessToken)
    localStorage.setItem(AUTH_REFRESH_KEY, refresh)
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData))
  }

  function clearAuth() {
    token.value = null
    refreshToken.value = null
    user.value = null
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_REFRESH_KEY)
    localStorage.removeItem(AUTH_USER_KEY)
  }

  return {
    user, token, refreshToken,
    isAuthenticated, isAdmin, isTenantAdmin, isUser, canEdit, canManageUsers,
    tenantId, userId,
    checkAuth, login, loginWithToken, logout, clearAuth
  }
})
